// The DOMAIN-FREE half of rule authoring, split out so the same rule shape can
// drive show/hide on any element: comparisons build against a named scope param,
// never a hardcoded pair. Feed pairing lives in feed-separator-rule.js.

export const JOIN_AND = "and";
export const JOIN_OR = "or";

const DAY_SECONDS = 86400;
const PATTERN_DATE = "YYYY-MM-DD";
const PATTERN_MONTH = "YYYY-MM";

// Operators are gated by the chosen field's type: LogicExpression throws on
// ordering comparisons between different scalar types, so offering `gt` on a
// string field would only ever produce a runtime error.
// Two kinds of condition, and the difference matters to the author:
//
//   SPLIT   "start a new heading each day" — breaks the feed wherever the value
//           changes, so the headings come from the data and you get as many as
//           the data needs.
//   MATCH   "the date is today" — picks out specific items, so you name the
//           heading yourself and get exactly the ones you defined.
//
// `group` drives the optgroup in the picker so the two never read as one list.
const GROUP_SPLIT = "Start a new heading when";
const GROUP_MATCH = "Only include items where";

export const OPERATORS = {
  datetime: [
    { id: "dayChanged", name: "the date moves to a new day", group: GROUP_SPLIT, takesValue: false },
    { id: "monthChanged", name: "the date moves to a new month", group: GROUP_SPLIT, takesValue: false },
    { id: "isToday", name: "the date is today", group: GROUP_MATCH, takesValue: false },
    { id: "isPast", name: "the date has already passed", group: GROUP_MATCH, takesValue: false },
    { id: "isFuture", name: "the date is still to come", group: GROUP_MATCH, takesValue: false },
    { id: "withinDays", name: "the date is in the next", group: GROUP_MATCH, takesValue: true, valueType: "number", suffix: "days" },
    { id: "afterDays", name: "the date is further ahead than", group: GROUP_MATCH, takesValue: true, valueType: "number", suffix: "days" },
    { id: "olderThanDays", name: "the date is further back than", group: GROUP_MATCH, takesValue: true, valueType: "number", suffix: "days" },
    { id: "onDate", name: "the date is exactly", group: GROUP_MATCH, takesValue: true, valueType: "date", placeholder: "2026-09-18" },
    { id: "beforeDate", name: "the date is before", group: GROUP_MATCH, takesValue: true, valueType: "date", placeholder: "2026-09-18" },
    { id: "afterDate", name: "the date is after", group: GROUP_MATCH, takesValue: true, valueType: "date", placeholder: "2026-09-18" },
  ],
  string: [
    { id: "changed", name: "the text changes", group: GROUP_SPLIT, takesValue: false },
    { id: "eq", name: "the text is exactly", group: GROUP_MATCH, takesValue: true, valueType: "string" },
    { id: "ne", name: "the text is anything except", group: GROUP_MATCH, takesValue: true, valueType: "string" },
    { id: "isEmpty", name: "the text is blank", group: GROUP_MATCH, takesValue: false },
    { id: "isNotEmpty", name: "the text is filled in", group: GROUP_MATCH, takesValue: false },
  ],
  int: [
    { id: "changed", name: "the number changes", group: GROUP_SPLIT, takesValue: false },
    { id: "eq", name: "the number is exactly", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "ne", name: "the number is anything except", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "lt", name: "the number is under", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "lte", name: "the number is at most", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "gt", name: "the number is over", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "gte", name: "the number is at least", group: GROUP_MATCH, takesValue: true, valueType: "number" },
    { id: "isZero", name: "the number is zero", group: GROUP_MATCH, takesValue: false },
  ],
  boolean: [
    { id: "changed", name: "the answer changes", group: GROUP_SPLIT, takesValue: false },
    { id: "isTrue", name: "the answer is yes", group: GROUP_MATCH, takesValue: false },
    { id: "isFalse", name: "the answer is no", group: GROUP_MATCH, takesValue: false },
  ],
};
// `float` and `denormalizedAggregate` compare like ints; `email`/`enum` like strings.
const TYPE_ALIASES = {
  float: "int",
  denormalizedAggregate: "int",
  email: "string",
  enum: "string",
};

export function normalizeFieldType(fieldType) {
  const bare = String(fieldType || "").trim();
  return TYPE_ALIASES[bare] || bare;
}

const ENGINE_STAMP_NOW = "now";

export function separatorFieldType(fieldDef) {
  if (fieldDef?.onCreate === ENGINE_STAMP_NOW || fieldDef?.onUpdate === ENGINE_STAMP_NOW) {
    return "datetime";
  }
  return normalizeFieldType(fieldDef?.type);
}

export function operatorsForFieldType(fieldType) {
  return OPERATORS[normalizeFieldType(fieldType)] || [];
}

export function findOperatorDefinition(fieldType, operatorId) {
  return operatorsForFieldType(fieldType).find(operator => operator.id === operatorId) || null;
}

export function buildParamSource(paramName, fieldName) {
  return { source: "params", paramName, path: fieldName };
}

export function buildDayBucket(paramName, fieldName) {
  return {
    ...buildParamSource(paramName, fieldName),
    transform: { type: "dateFormat", params: { pattern: PATTERN_DATE } },
  };
}

export function buildMonthBucket(paramName, fieldName) {
  return {
    ...buildParamSource(paramName, fieldName),
    transform: { type: "dateFormat", params: { pattern: PATTERN_MONTH } },
  };
}

function buildShiftedNow(seconds) {
  if (seconds === 0) {
    return { source: "currentTimestamp" };
  }
  return {
    source: "currentTimestamp",
    transform: {
      type: seconds > 0 ? "add" : "subtract",
      params: { quantity: Math.abs(seconds) },
    },
  };
}

// A BOUNDARY condition asks "did something change across this gap?".
// A BUCKET condition asks "which group is the row after the gap in?": and on
// its own that is not enough: two consecutive rows in the SAME bucket would both
// match and the header would repeat. A bucket condition therefore emits
//
//     after-is-in-bucket  AND  NOT before-is-in-bucket
//
// so it fires only on the row that ENTERS the bucket. `leading` still covers the
// very first row, where `before` is null.
export function buildBucketMembership(paramName, fieldName, operator, value) {
  if (operator === "isToday") {
    return {
      op: "eq",
      left: buildDayBucket(paramName, fieldName),
      right: {
        source: "currentTimestamp",
        transform: { type: "dateFormat", params: { pattern: PATTERN_DATE } },
      },
    };
  }
  if (operator === "onDate") {
    return {
      op: "eq",
      left: buildDayBucket(paramName, fieldName),
      right: { source: "constant", value: String(value ?? "") },
    };
  }
  if (operator === "beforeDate" || operator === "afterDate") {
    return {
      op: operator === "beforeDate" ? "lt" : "gt",
      left: buildDayBucket(paramName, fieldName),
      right: { source: "constant", value: String(value ?? "") },
    };
  }
  if (operator === "isPast" || operator === "isFuture") {
    return {
      op: operator === "isPast" ? "lt" : "gte",
      left: buildParamSource(paramName, fieldName),
      right: { source: "currentTimestamp" },
    };
  }
  const days = Math.round(Number(value) || 0);
  if (operator === "olderThanDays") {
    return {
      op: "lt",
      left: buildParamSource(paramName, fieldName),
      right: buildShiftedNow(-days * DAY_SECONDS),
    };
  }
  return {
    op: operator === "withinDays" ? "lte" : "gt",
    left: buildParamSource(paramName, fieldName),
    right: buildShiftedNow(days * DAY_SECONDS),
  };
}

export const SPLIT_OPERATORS = new Set(["dayChanged", "monthChanged", "changed"]);

export const BUCKET_OPERATORS = new Set([
  "isToday",
  "isPast",
  "isFuture",
  "withinDays",
  "afterDays",
  "olderThanDays",
  "onDate",
  "beforeDate",
  "afterDate",
]);

// Numeric-looking input becomes a number so an ordering comparison against a
// numeric field does not throw on mixed types. An explicit type wins when the
// caller has one; otherwise the text decides.
function coerceLiteral(value, type) {
  const text = String(value ?? "").trim();
  if (type === "string") {
    return text;
  }
  if (text !== "" && Number.isFinite(Number(text))) {
    return Number(text);
  }
  return text;
}

export function buildComparison(condition, scope = "after", pairBuilder = null) {
  const { fieldName, fieldType, operator, value } = condition;
  const type = normalizeFieldType(fieldType);

  // SPLIT operators compare two subjects, so they cannot be expressed against a
  // single scope -- only the host holding a pair can build the comparison.
  if (SPLIT_OPERATORS.has(operator)) {
    return pairBuilder(condition);
  }
  if (operator === "isEmpty" || operator === "isNotEmpty") {
    return {
      op: operator === "isEmpty" ? "eq" : "ne",
      left: buildParamSource(scope, fieldName),
      right: { source: "constant", value: "" },
    };
  }
  if (operator === "isZero") {
    return {
      op: "eq",
      left: buildParamSource(scope, fieldName),
      right: { source: "constant", value: 0 },
    };
  }
  if (BUCKET_OPERATORS.has(operator)) {
    // Membership only. The "did the bucket CHANGE?" half is added by
    // buildVisibilityExpression, which alone knows the preceding rules.
    return buildBucketMembership(scope, fieldName, operator, value);
  }
  if (operator === "isTrue" || operator === "isFalse") {
    return {
      op: "eq",
      left: buildParamSource(scope, fieldName),
      right: { source: "constant", value: operator === "isTrue" },
    };
  }

  // Plain comparisons against a literal the author typed. The coercion is
  // driven by the VALUE, not by `fieldType`: persisted rules store only the
  // field NAME, so at render time there is no type hint to read, and a numeric
  // field compared against the string "0" makes LogicExpression throw.
  return {
    op: operator,
    left: buildParamSource(scope, fieldName),
    right: { source: "constant", value: coerceLiteral(value, type) },
  };
}

